// src/modules/appointments/components/AppointmentList.jsx
import PropTypes from "prop-types";

function formatDateTime(value, timeZone) {
  if (!value) return "-";
  const raw = String(value);
  const normalized = raw.replace(" ", "T");
  const hasExplicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  if (!hasExplicitZone) {
    const [date = "", time = ""] = normalized.split("T");
    const [year, month, day] = date.split("-");
    const [hour = "", minute = ""] = time.split(":");
    if (year && month && day && hour && minute) {
      return `${day}/${month}/${year}, ${hour}:${minute}`;
    }
  }
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("es-EC", {
    timeZone: timeZone || undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusPill({ status }) {
  const s = (status || "sin_estado").toLowerCase();

  const label =
    s === "pending" || s === "pending_approval" || s === "pendiente"
      ? "Pendiente"
      : s === "confirmed" || s === "confirmada"
      ? "Confirmada"
      : s === "cancelled" || s === "canceled" || s === "cancelada"
      ? "Cancelada"
      : s === "completed"
      ? "Completada"
      : status || "Sin estado";

  const cls =
    s === "confirmed" || s === "confirmada"
      ? "is-confirmed"
    : s === "cancelled" || s === "canceled" || s === "cancelada"
      ? "is-canceled"
    : s === "completed" || s === "completada"
      ? "is-completed"
      : "is-pending";

  return <span className={`appointment-status-pill ${cls}`}>{label}</span>;
}

StatusPill.propTypes = {
  status: PropTypes.any,
};

export default function AppointmentList({
  items,
  onSelect,
  onQuickStatus,
  onQuickPayment,
}) {
  const list = Array.isArray(items) ? items : [];

  if (!list.length) {
    return (
      <div className="text-xs text-[color:var(--muted)]">
        No hay citas para mostrar con los criterios actuales.
      </div>
    );
  }

  return (
    <div className="overflow-auto max-h-[calc(100vh-260px)] border border-gray-200 rounded-md">
      <table className="w-full text-[10px] border-collapse">
        <thead className="bg-gray-50 text-[9px] uppercase tracking-wide text-gray-500">
          <tr>
            <th className="text-left px-2 py-1">Fecha / hora</th>
            <th className="text-left px-2 py-1">Cliente</th>
            <th className="text-left px-2 py-1">Servicio</th>
            <th className="text-left px-2 py-1">Estado</th>
            <th className="text-left px-2 py-1">Total</th>
            <th className="text-left px-2 py-1">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {list.map((a) => {
            const id = a.id ?? a.uuid ?? a._id ?? null;
            const timeZone = a.timezone || a.location_timezone || undefined;

            return (
              <tr
                key={id || `${a.start_at}-${a.client_name}-${Math.random()}`}
                className="border-t hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => onSelect && onSelect(a)}
              >
                <td className="px-2 py-1 align-top">
                  <div className="font-medium text-[10px] text-gray-800">
                    {formatDateTime(a.start_at || a.date, timeZone)}
                  </div>
                  {a.end_at && (
                    <div className="text-[8px] text-gray-500">
                      hasta {formatDateTime(a.end_at, timeZone)}
                    </div>
                  )}
                </td>

                <td className="px-2 py-1 align-top">
                  <div className="text-[10px] text-gray-800">
                    {a.subject_name || a.client_name || a.customer_name || "-"}
                  </div>
                  {(a.subject_phone || a.client_phone || a.phone) && (
                    <div className="text-[8px] text-gray-500">
                      {a.subject_phone || a.client_phone || a.phone}
                    </div>
                  )}
                </td>

                <td className="px-2 py-1 align-top">
                  <div className="text-[9px] text-gray-700 line-clamp-2">
                    {a.service_name || a.reason_name || a.title || a.description || "—"}
                  </div>
                  {a.resource_names && (
                    <div className="text-[8px] text-gray-500">
                      {a.resource_names}
                    </div>
                  )}
                </td>

                <td className="px-2 py-1 align-top">
                  <StatusPill status={a.status} />
                </td>

                <td className="px-2 py-1 align-top text-right">
                  <div className="text-[9px] text-gray-800">
                    {a.total_amount
                      ? `$${Number(a.total_amount).toFixed(2)}`
                      : a.total
                      ? `$${Number(a.total).toFixed(2)}`
                      : a.service_price
                      ? `$${Number(a.service_price).toFixed(2)}`
                      : "—"}
                  </div>
                </td>

                <td className="px-2 py-1 align-top">
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      className="px-2 py-[2px] text-[8px] rounded bg-[color:var(--primary)] text-white hover:opacity-90"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect && onSelect(a);
                      }}
                    >
                      Ver
                    </button>
                    <button
                      type="button"
                      className="px-2 py-[2px] text-[8px] rounded border border-emerald-500 text-emerald-200 hover:bg-emerald-900/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQuickStatus && onQuickStatus(a, "confirmed");
                      }}
                    >
                      Confirmar
                    </button>
                    <button
                      type="button"
                      className="px-2 py-[2px] text-[8px] rounded border border-sky-500 text-sky-200 hover:bg-sky-900/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQuickStatus && onQuickStatus(a, "completed");
                      }}
                    >
                      Completar
                    </button>
                    <button
                      type="button"
                      className="px-2 py-[2px] text-[8px] rounded border border-amber-500 text-amber-200 hover:bg-amber-900/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQuickPayment && onQuickPayment(a);
                      }}
                    >
                      Pago
                    </button>
                    <button
                      type="button"
                      className="px-2 py-[2px] text-[8px] rounded border border-red-500 text-red-200 hover:bg-red-900/30"
                      onClick={(e) => {
                        e.stopPropagation();
                        onQuickStatus && onQuickStatus(a, "canceled");
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

AppointmentList.propTypes = {
  items: PropTypes.any,
  onSelect: PropTypes.func,
  onQuickStatus: PropTypes.func,
  onQuickPayment: PropTypes.func,
};
